import { Card, Tabs, Empty, Alert, Typography, Space, Divider, Button } from 'antd';
import { FileExclamationOutlined, BulbOutlined, DownloadOutlined, FilePdfOutlined, FileTextOutlined } from '@ant-design/icons';
import type { NachaParseResult } from '@/types/nacha';
import { trackViewChange, trackExport } from '@/utils/analytics';
import { exportToJson, exportToCsv, exportToPdf, downloadFile } from '@/utils/export';
import SummaryView from './views/SummaryView';
import BatchesView from './views/BatchesView';
import EntriesView from './views/EntriesView';

const { Title, Text, Paragraph } = Typography;

interface ResultViewProps {
  result: NachaParseResult | null;
  loading?: boolean;
  loadingText?: string;
}

function ResultView({ result, loading = false, loadingText = 'Processing...' }: ResultViewProps) {
  // Show parse errors if parsing failed
  if (result && !result.success && !result.file) {
    return (
      <Card 
        title={
          <Space>
            <FileTextOutlined />
            Parse Results
          </Space>
        }
        className="result-view-card error-card" 
        style={{ height: '100%' }}
      >
        <div className="error-container">
          <div className="error-icon-wrapper">
            <FileExclamationOutlined className="error-icon" />
          </div>
          
          <Title level={3} style={{ marginTop: 24, marginBottom: 8 }}>
            Parse Error
          </Title>
          
          <Paragraph style={{ fontSize: 14, marginBottom: 24, opacity: 0.8 }}>
            We couldn't parse this file. Please check the errors below.
          </Paragraph>

          <div className="error-details">
            {result.validation.errors.map((error, index) => (
              <Alert
                key={index}
                message={
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text strong style={{ fontSize: 14 }}>
                      {error.field}
                    </Text>
                    <Text style={{ fontSize: 13 }}>
                      {error.message}
                    </Text>
                    {error.location && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Location: {error.location}
                      </Text>
                    )}
                  </Space>
                }
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
              />
            ))}
          </div>

          <Divider style={{ margin: '24px 0' }} />

          <Alert
            message="Troubleshooting Tips"
            description={
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text>• Ensure the file is in valid NACHA/ACH format</Text>
                <Text>• Check that file header starts with '1' (File Header Record)</Text>
                <Text>• Verify all records are 94 characters long</Text>
                <Text>• Make sure there are no extra spaces or line breaks</Text>
              </Space>
            }
            type="info"
            showIcon
            icon={<BulbOutlined />}
          />
        </div>

        <style>{`
          .error-card .ant-card-body {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 48px 32px;
          }

          .error-container {
            max-width: 600px;
            width: 100%;
            text-align: center;
            animation: errorFadeIn 0.5s ease-out;
          }

          @keyframes errorFadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .error-icon-wrapper {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
            animation: errorPulse 2s ease-in-out infinite;
          }

          [data-theme='light'] .error-icon-wrapper {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.08) 100%);
          }

          @keyframes errorPulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
            }
            50% {
              box-shadow: 0 0 0 20px rgba(239, 68, 68, 0);
            }
          }

          .error-icon {
            font-size: 40px;
            color: var(--error);
            animation: errorShake 0.5s ease-out;
          }

          @keyframes errorShake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-10px); }
            75% { transform: translateX(10px); }
          }

          .error-details {
            text-align: left;
            margin-bottom: 16px;
            max-height: 300px;
            overflow-y: auto;
            padding: 0 4px;
          }

          .error-details .ant-alert {
            border-radius: 12px;
            transition: all 0.3s ease;
          }

          .error-details .ant-alert:hover {
            transform: translateX(4px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
          }
        `}</style>
      </Card>
    );
  }
  
  // Show loading state
  if (loading) {
    return (
      <Card 
        title={
          <Space>
            <FileTextOutlined />
            Parse Results
          </Space>
        }
        className="result-view-card" 
        style={{ height: '100%' }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 24
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            border: '4px solid var(--border-primary)',
            borderTopColor: 'var(--accent-blue)',
            animation: 'spin 1s linear infinite'
          }} />
          <Text style={{
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--accent-blue)',
            textAlign: 'center',
            maxWidth: '80%'
          }}>
            {loadingText}
          </Text>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </Card>
    );
  }
  
  if (!result || !result.file) {
    return (
      <Card 
        title={
          <Space>
            <FileTextOutlined />
            Parse Results
          </Space>
        }
        className="result-view-card" 
        style={{ height: '100%' }}
      >
        <Empty
          description="Please upload or paste NACHA file content and parse"
          style={{ marginTop: '20%' }}
        />
      </Card>
    );
  }

  // Calculate total entries for export tracking
  const totalEntries = result.file.batches.reduce((sum, batch) => sum + batch.entries.length, 0);

  // Export handlers
  const handleExportJson = () => {
    const json = exportToJson(result);
    downloadFile(json, 'nacha-export.json', 'application/json');
    trackExport('JSON', totalEntries);
  };

  const handleExportCsv = () => {
    const csv = exportToCsv(result);
    downloadFile(csv, 'nacha-export.csv', 'text/csv');
    trackExport('CSV', totalEntries);
  };

  const handleExportPdf = () => {
    exportToPdf(result);
    trackExport('PDF', totalEntries);
  };

  const tabItems = [
    {
      key: 'summary',
      label: 'Summary',
      children: <SummaryView file={result.file} validation={result.validation} />,
    },
    {
      key: 'batches',
      label: 'Batches',
      children: <BatchesView batches={result.file.batches} />,
    },
    {
      key: 'entries',
      label: 'Entries',
      children: <EntriesView batches={result.file.batches} />,
    },
  ];

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          Parse Results
        </Space>
      }
      className="result-view-card"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      extra={
        <Space>
          <Button icon={<DownloadOutlined />} onClick={handleExportJson}>
            JSON
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExportCsv}>
            CSV
          </Button>
          <Button icon={<FilePdfOutlined />} onClick={handleExportPdf}>
            PDF
          </Button>
        </Space>
      }
    >
      <Tabs
        defaultActiveKey="summary"
        items={tabItems}
        style={{ flex: 1 }}
        className="result-tabs fade-in"
        size="large"
        onChange={(key) => {
          // Track view changes
          const viewNames: Record<string, string> = {
            'summary': 'Summary',
            'batches': 'Batches',
            'entries': 'Entries'
          };
          trackViewChange(viewNames[key] || key);
        }}
      />
    </Card>
  );
}

export default ResultView;
